import { BookOpen, Check, Link2 } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCopTopUpFormat } from "@/modules/dashboard/cards/components/cop-top-up/copTopUpFormat";
import { CopTopUpRateRow } from "@/modules/dashboard/cards/components/cop-top-up/CopTopUpRateRow";
import { formatCopRate } from "@/modules/dashboard/cards/utils/cardCopTopUp";
import { usePayinBrebOrigin } from "@/modules/dashboard/payins/hooks/usePayinBrebOrigin";
import { ledgerPathForOperationType } from "@/modules/dashboard/payments-ledger/utils/ledgerLinks";
import { WalletDebitManage } from "@/modules/dashboard/wallet-debits/components/WalletDebitManage";
import { WalletDebitStatusPill } from "@/modules/dashboard/wallet-debits/components/WalletDebitStatusPill";
import { useWalletDebit } from "@/modules/dashboard/wallet-debits/hooks/useWalletDebit";
import type { WalletDebitStatus } from "@/modules/dashboard/wallet-debits/types/walletDebits.types";
import {
  describeDebitSend,
  isCancelledColocaDebit,
  isColocaDebit,
} from "@/modules/dashboard/wallet-debits/utils/walletDebit";
import { WalletTopUpReceiptActions } from "@/modules/dashboard/wallet-top-ups/components/WalletTopUpReceiptActions";
import { WALLET_TOP_UP_ORIGIN } from "@/modules/dashboard/wallet-top-ups/constants/walletTopUps.constants";
import { useWalletTopUp } from "@/modules/dashboard/wallet-top-ups/hooks/useWalletTopUp";
import { useWalletTopUpAccess } from "@/modules/dashboard/wallet-top-ups/hooks/useWalletTopUpAccess";
import { isColocaRefund, isColocaTopUp } from "@/modules/dashboard/wallet-top-ups/utils/walletTopUp";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { TransferReceiptActions } from "@/shared/components/payments/TransferReceiptActions";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { splitFeeTotal, taxLinesFromBreakdown } from "@/shared/utils/paymentTaxes";
import { formatRail } from "@/shared/utils/railDisplay";
import { payoutBrebKey } from "@/shared/utils/transferReceipt/transferReceipt";

import { STATEMENT_MOVEMENT_PARAM } from "../constants/wallets.constants";
import { useStatementPayin } from "../hooks/useStatementPayin";
import { useStatementPayout } from "../hooks/useStatementPayout";
import { useWalletEntryLabels } from "../hooks/useWalletEntryLabels";
import type {
  LedgerOperationType,
  RuntimeWallet,
  RuntimeWalletTransaction,
} from "../types/payments-wallets.types";
import { currencyFractionDigits, resolveLocale } from "../utils/formatWalletAmount";

/** Opens this movement on the wallet's statement, whatever page or filter it falls under. */
function statementMovementLink(walletId: string, movementId: string): string {
  return `${window.location.origin}/app/wallets/${walletId}/movements?${STATEMENT_MOVEMENT_PARAM}=${movementId}`;
}

interface WalletStatementDetailDialogProps {
  transaction: RuntimeWalletTransaction | null;
  /** Only its id (for the link) and currency are read, so the staff movements list can pass a leg's own. */
  wallet: Pick<RuntimeWallet, "id" | "currency">;
  operationLabels: Record<LedgerOperationType, string>;
  onClose: () => void;
  /** Staff lists that cross organizations name the one the movement belongs to. */
  organizationName?: string | null;
  /** Rendered under the actions: the staff incident panel. */
  extra?: ReactNode;
  size?: "md" | "lg";
}

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border py-2.5 last:border-b-0">
    <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
    <span className="text-right text-sm font-medium text-foreground">{value}</span>
  </div>
);

/**
 * Read-only detail of a single ledger leg (opened from a statement row). A side drawer rather than a centred
 * dialog, matching the activity screen: the reader is scanning a list and comparing rows, which a panel that
 * leaves the list in place supports and a modal over the middle of it does not.
 */
const MEMBER_DEBIT_STATUS = {
  RESERVED: "member_status_reserved",
  SENT: "member_status_sent",
  CANCELLED: "member_status_cancelled",
} as const satisfies Record<WalletDebitStatus, string>;

export const WalletStatementDetailDialog = ({
  transaction,
  wallet,
  operationLabels,
  onClose,
  organizationName,
  extra,
  size = "md",
}: WalletStatementDetailDialogProps) => {
  const { t, activeLanguage } = useTranslations("wallets");
  const taxLabel = useTaxLabel();
  const { t: tPayins } = useTranslations("payins");
  const { describe, statusOf } = useWalletEntryLabels();
  const describedEntry = transaction ? describe(transaction) : null;
  const { copiedValue, copy } = useCopyToClipboard(t("statement_detail_copy_link_error"));
  const movementLink = transaction ? statementMovementLink(wallet.id, transaction.id) : "";
  const linkCopied = !!movementLink && copiedValue === movementLink;
  const operationStatus = transaction ? statusOf(transaction) : null;
  const { isInternalUser, user } = useAuth();
  const navigate = useNavigate();
  const decimals = currencyFractionDigits(wallet.currency);

  /**
   * This dialog shows the wallet's own side of one journal entry; the full double-entry trail lives in the
   * ledger view. INTERNAL staff only — the ledger endpoint is `userTypes: [INTERNAL]` — and only for the
   * operation types that name a browsable operation (a `REVERSE` leg belongs to the entry it mirrors).
   */
  const ledgerPath = transaction
    ? ledgerPathForOperationType(transaction.operation_type, transaction.operation_id)
    : null;

  const formattedDate = transaction
    ? new Intl.DateTimeFormat(resolveLocale(activeLanguage), {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(transaction.posted_at))
    : "";
  const inbound = transaction?.direction === "C";
  const isPayinLeg = transaction?.operation_type === "PAYIN";
  const { payin, isLoading: isPayinLoading } = useStatementPayin(
    isPayinLeg && transaction ? transaction.operation_id : null,
  );
  const origin = usePayinBrebOrigin(payin?.breb);
  const isBrebPayoutLeg = transaction?.operation_type === "PAYOUT" && transaction.operation_rail === "BREB";
  const brebPayout = useStatementPayout(isBrebPayoutLeg && transaction ? transaction.operation_id : null);
  const brebKey = payoutBrebKey(brebPayout?.receipt);
  const brebNetworkReference = brebPayout?.receipt?.network_reference?.trim() || null;
  const isTopUpLeg = transaction ? isColocaTopUp(transaction) : false;
  const isRefundLeg = transaction ? isColocaRefund(transaction) : false;
  const { topUp, isLoading: isTopUpLoading } = useWalletTopUp(
    isTopUpLeg && transaction ? transaction.operation_id : null,
  );
  // Members read a debit's concept and status; staff also see the send, its proof and its history.
  const isDebitLeg = transaction ? isColocaDebit(transaction) : false;
  const {
    debit,
    isLoading: isDebitLoading,
    replace: replaceDebit,
  } = useWalletDebit(isDebitLeg && transaction ? transaction.operation_id : null);
  const { allowed: canManageDebits } = useWalletTopUpAccess();
  const { t: tDebits } = useTranslations("walletDebits");
  // Staff only: members get no `net_amount`.
  const debitSend = debit?.net_amount ? describeDebitSend(debit, activeLanguage) : null;
  const toppedUpCard = transaction?.operation_card_top_up ?? null;
  // The rate is what the load came to; on its reversal the pesos came back and no rate applies.
  const cardLoad = toppedUpCard && transaction?.operation_type !== "REVERSE" ? toppedUpCard : null;
  const formatCopTopUp = useCopTopUpFormat();
  const typeLabel = transaction
    ? isTopUpLeg
      ? t(isRefundLeg ? "tx_op_coloca_refund" : "tx_op_coloca_top_up")
      : isDebitLeg
        ? t(isCancelledColocaDebit(transaction) ? "tx_op_coloca_debit_cancelled" : "tx_op_coloca_debit")
        : toppedUpCard
          ? t(transaction.operation_type === "REVERSE" ? "tx_op_card_top_up_reversal" : "tx_op_card_top_up")
          : operationLabels[transaction.operation_type]
    : "";
  const payerSent = payin ? (payin.gross_amount_received ?? payin.gross_amount_expected) : null;
  const payinFee = payin
    ? splitFeeTotal(payin.fee_total, taxLinesFromBreakdown(payin.fee_breakdown?.taxes))
    : null;
  const amountProps = {
    currency: transaction?.currency ?? wallet.currency,
    minimumFractionDigits: decimals.min,
    maximumFractionDigits: decimals.max,
    locale: resolveLocale(activeLanguage),
    size: "sm" as const,
  };

  return (
    <ColocaDrawer
      open={transaction !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size={size}
      // The action row is pinned to the bottom only when it is the last thing; under it, the staff incident
      // panel is content and must scroll with the rows.
      pinLastChild={!extra}
      title={typeLabel}
      description={transaction ? `${wallet.currency} · ${formattedDate}` : undefined}
    >
      {transaction && (
        <div className="flex flex-col">
          {organizationName && (
            <DetailRow label={t("statement_detail_organization")} value={organizationName} />
          )}
          <DetailRow label={t("statement_detail_date")} value={formattedDate} />
          <DetailRow label={t("statement_detail_type")} value={typeLabel} />
          {transaction.operation_rail && !toppedUpCard && (
            <DetailRow label={t("statement_detail_rail")} value={formatRail(transaction.operation_rail)} />
          )}
          {toppedUpCard && (
            <DetailRow
              label={t("statement_detail_card")}
              value={
                <span className="flex items-center gap-2">
                  <span className="font-mono">•••• {toppedUpCard.last_4 ?? ""}</span>
                  <ColocaButton
                    variant="link"
                    size="xs"
                    onClick={() => navigate(`/app/cards/cards/${toppedUpCard.card_id}`)}
                  >
                    {t("statement_detail_card_open")}
                  </ColocaButton>
                </span>
              }
            />
          )}
          {cardLoad && (
            <CopTopUpRateRow
              rates={cardLoad}
              locale={activeLanguage}
              className="-mx-1 rounded-md border-b border-border px-3 py-3"
            />
          )}
          {cardLoad?.credited_amount && (
            <DetailRow
              label={t("statement_detail_card_credited")}
              value={
                <span className="font-mono tabular-nums">
                  {`${cardLoad.cop_per_usd_estimated ? "≈ " : ""}${formatCopTopUp(cardLoad.credited_amount, "USD")}`}
                </span>
              }
            />
          )}
          {cardLoad && formatCopRate(cardLoad.cop_per_usdt, "USDT", activeLanguage) && (
            <DetailRow
              label={t("statement_detail_card_price")}
              value={
                <span className="font-mono tabular-nums">
                  {formatCopRate(cardLoad.cop_per_usdt, "USDT", activeLanguage)}
                </span>
              }
            />
          )}
          {transaction.operation_beneficiary_name && (
            <DetailRow
              label={t("statement_detail_beneficiary")}
              value={transaction.operation_beneficiary_name}
            />
          )}
          {brebKey && <DetailRow label={t("statement_detail_breb_key")} value={brebKey} />}
          {brebNetworkReference && (
            <DetailRow
              label={t("statement_detail_breb_network_reference")}
              value={<span className="font-mono text-xs break-all">{brebNetworkReference}</span>}
            />
          )}
          <DetailRow
            label={t("statement_detail_direction")}
            value={inbound ? t("statement_filter_dir_credit") : t("statement_filter_dir_debit")}
          />
          {isPayinLeg && isPayinLoading && (
            <div className="space-y-2 border-b border-border py-3" aria-busy="true">
              <ColocaSkeleton className="h-4 w-full" />
              <ColocaSkeleton className="h-4 w-2/3" />
            </div>
          )}
          {isTopUpLeg && <DetailRow label={t("statement_detail_origin")} value={WALLET_TOP_UP_ORIGIN} />}
          {isTopUpLeg && isTopUpLoading && (
            <div className="space-y-2 border-b border-border py-3" aria-busy="true">
              <ColocaSkeleton className="h-4 w-full" />
              <ColocaSkeleton className="h-4 w-2/3" />
            </div>
          )}
          {topUp && <DetailRow label={t("statement_detail_concept")} value={topUp.concept} />}
          {isDebitLeg && isDebitLoading && (
            <div className="space-y-2 border-b border-border py-3" aria-busy="true">
              <ColocaSkeleton className="h-4 w-full" />
              <ColocaSkeleton className="h-4 w-2/3" />
            </div>
          )}
          {debit && <DetailRow label={t("statement_detail_concept")} value={debit.concept} />}
          {debit && (
            <DetailRow
              label={tDebits("member_status")}
              value={
                isInternalUser ? (
                  <WalletDebitStatusPill debit={debit} />
                ) : (
                  tDebits(MEMBER_DEBIT_STATUS[debit.status])
                )
              }
            />
          )}
          {debit?.internal_note && (
            <DetailRow label={t("statement_detail_internal_note")} value={debit.internal_note} />
          )}
          {debitSend && (
            <>
              <DetailRow
                label={tDebits("review_corridor")}
                value={debitSend.rail ?? tDebits("corridor_other")}
              />
              <DetailRow label={tDebits("review_send_by_hand")} value={debitSend.sent} />
              {debitSend.commission && debitSend.taxes && (
                <DetailRow
                  label={tDebits("done_fees")}
                  value={tDebits("send_preview_fees", {
                    commission: debitSend.commission,
                    taxes: debitSend.taxes,
                  })}
                />
              )}
            </>
          )}
          {debit?.created_by_email && (
            <DetailRow label={t("statement_detail_created_by")} value={debit.created_by_email} />
          )}
          {origin.channel && <DetailRow label={tPayins("payin_detail_channel")} value={origin.channel} />}
          {origin.receivingKey && (
            <DetailRow label={tPayins("payin_detail_receiving_key")} value={origin.receivingKey} />
          )}
          {origin.payer && <DetailRow label={tPayins("payin_detail_payer")} value={origin.payer} />}
          {payerSent && (
            <DetailRow
              label={tPayins("payin_detail_sent")}
              value={<ColocaAmountDisplay value={payerSent} {...amountProps} />}
            />
          )}
          {payinFee && (
            <DetailRow
              label={tPayins("payin_detail_fee")}
              value={<ColocaAmountDisplay value={payinFee.commission} {...amountProps} />}
            />
          )}
          {payinFee?.taxes.map((tax) => (
            <DetailRow
              key={tax.code}
              label={taxLabel(tax)}
              value={<ColocaAmountDisplay value={tax.amount} {...amountProps} />}
            />
          ))}
          <DetailRow
            label={isPayinLeg ? tPayins("payin_detail_credited") : t("statement_detail_amount")}
            value={
              <ColocaAmountDisplay
                // `amount` is always positive (handoff §3.5) — the sign comes from the direction.
                value={inbound ? transaction.amount : `-${transaction.amount}`}
                {...amountProps}
                signed
              />
            }
          />
          <DetailRow
            label={t("statement_detail_balance_after")}
            value={<ColocaAmountDisplay value={transaction.balance_after} {...amountProps} />}
          />
          {describedEntry && <DetailRow label={t("statement_detail_description")} value={describedEntry} />}
          {/* The operation's CURRENT status, which the leg alone cannot tell: a payout only touches this
              wallet on its reserve entry, so the description stays "salida de dispersión" even once paid. */}
          {operationStatus && <DetailRow label={t("statement_detail_status")} value={operationStatus} />}
          {topUp ? (
            <DetailRow
              label={t("statement_detail_top_up_reference")}
              value={<span className="font-mono text-xs">{topUp.reference}</span>}
            />
          ) : debit ? (
            <DetailRow
              label={t("statement_detail_debit_reference")}
              value={<span className="font-mono text-xs">{debit.reference}</span>}
            />
          ) : (
            <DetailRow
              label={t("statement_detail_reference")}
              value={<span className="break-all font-mono text-xs">{transaction.operation_id}</span>}
            />
          )}
          {transaction.reverses_entry_id && (
            <DetailRow label={t("statement_detail_reversal")} value={t("statement_detail_reversal_yes")} />
          )}
          {topUp && <WalletTopUpReceiptActions topUp={topUp} className="pt-3" />}
          {debit && isInternalUser && (
            <div className="pt-4">
              <WalletDebitManage debit={debit} canEdit={canManageDebits} onChange={replaceDebit} />
            </div>
          )}
          {transaction.operation_type === "PAYOUT" && (
            <TransferReceiptActions
              payoutId={transaction.operation_id}
              status={transaction.operation_status}
              organizationName={isInternalUser ? null : (user?.organization?.name ?? null)}
              size="sm"
              className="pt-3"
            />
          )}
          <div className="flex flex-wrap gap-2 pt-3">
            <ColocaButton
              variant="default"
              size="sm"
              icon={linkCopied ? Check : Link2}
              onClick={() => void copy(movementLink, t("statement_detail_link_copied"))}
            >
              {linkCopied ? t("statement_detail_link_copied") : t("statement_detail_copy_link")}
            </ColocaButton>
            {isInternalUser && ledgerPath && (
              <ColocaButton variant="default" size="sm" icon={BookOpen} onClick={() => navigate(ledgerPath)}>
                {t("statement_detail_view_ledger")}
              </ColocaButton>
            )}
          </div>
          {extra && <div className="pt-5">{extra}</div>}
        </div>
      )}
    </ColocaDrawer>
  );
};
